import React, { useContext, useEffect, useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { Button as ButtonLib, ColorPicker } from "@npm_leadtech/qr-ui-components";
import { QREditorContext } from "@/contexts/QREditorContext";
import { checkValidHexColor } from "@/utils/formHelper";
import { ColorGroupThemePickerControlledProps, FormState, FormValues } from "./types";
import { useFormContext } from "react-hook-form";
import { useDebounce } from "@/hooks/utils/useDebounce";
import "./styles.scss";

/**
 * Componente específico para manejo de themes
 *
 * @note Este componente es una versión modificada de ColorGroupControlled
 *       con lógica específica para themes. La duplicación es necesaria ya que
 *       la lógica original no puede ser reutilizada directamente.
 */
export const ColorGroupThemePickerControlled = (props: ColorGroupThemePickerControlledProps) => {
  const {
    primaryColor: { id: primaryColorId, label: primaryColorLabel, color: primaryColor },
    secondaryColor: { id: secondaryColorId, label: secondaryColorLabel, color: secondaryColor },
    className,
    rules,
  } = props;

  const qrEditorContext = useContext(QREditorContext);
  const { qrData, setQrData } = qrEditorContext ?? {};
  const methods = useFormContext<FormValues>();
  const [buttonColor, setButtonColor] = useState(primaryColor);
  const [backgroundColor, setBackgroundColor] = useState(secondaryColor);

  const { clearErrors, setError, getValues, setValue, formState } = methods;
  const { errors } = formState as FormState;

  const debouncedButtonColor = useDebounce(buttonColor, 50);
  const debouncedBackgroundColor = useDebounce(backgroundColor, 50);

  const handleSwapColors = () => {
    if (!qrEditorContext) {
      return null;
    }

    const isValidButtonColor = isValidColor(buttonColor, "themeColor.buttonColor");
    const isValidBackgroundColor = isValidColor(backgroundColor, "themeColor.backgroundColor");

    if (isValidButtonColor && isValidBackgroundColor) {
      clearErrors("themeColor.buttonColor");
      clearErrors("themeColor.backgroundColor");

      setButtonColor(backgroundColor);
      setBackgroundColor(buttonColor);

      setValue("themeColor", {
        buttonColor: backgroundColor,
        backgroundColor: buttonColor,
      });
    }
  };

  const isValidColor = (
    color: string,
    fieldName: "themeColor.buttonColor" | "themeColor.backgroundColor"
  ) => {
    const isValid = checkValidHexColor(color);

    if (rules?.required?.value && !color) {
      setError(fieldName, {
        type: "required",
        message: rules.required.message || "Required field",
      });
      return false;
    }

    if (!isValid) {
      setError(fieldName, {
        type: "pattern",
        message: rules.pattern.message || "The color is not valid",
      });
      return false;
    }

    return true;
  };

  const handleButtonColorChange = (e: any) => {
    setButtonColor(e);
  };

  const handleBackgroundColorChange = (e: any) => {
    setBackgroundColor(e);
  };

  useEffect(() => {
    const isValid = isValidColor(debouncedButtonColor, "themeColor.buttonColor");
    setValue("themeColor", {
      ...getValues("themeColor"),
      buttonColor: debouncedButtonColor,
    });

    if (isValid) {
      clearErrors("themeColor.buttonColor");
    }
  }, [debouncedButtonColor]);

  useEffect(() => {
    const isValid = isValidColor(debouncedBackgroundColor, "themeColor.backgroundColor");
    setValue("themeColor", {
      ...getValues("themeColor"),
      backgroundColor: debouncedBackgroundColor,
    });

    if (isValid) {
      clearErrors("themeColor.backgroundColor");
    }
  }, [debouncedBackgroundColor]);

  useEffect(() => {
    if (!qrEditorContext) return;

    const validButtonColor =
      checkValidHexColor(debouncedButtonColor) || debouncedButtonColor === "";
    const validBackgroundColor =
      checkValidHexColor(debouncedBackgroundColor) || debouncedBackgroundColor === "";

    if (!validButtonColor || !validBackgroundColor) return;

    const nextButtonColor = debouncedButtonColor === "" ? primaryColor : debouncedButtonColor;
    const nextBackgroundColor =
      debouncedBackgroundColor === "" ? secondaryColor : debouncedBackgroundColor;

    if (
      qrData?.data?.themeColor?.buttonColor === nextButtonColor &&
      qrData?.data?.themeColor?.backgroundColor === nextBackgroundColor
    ) {
      return;
    }

    setQrData({
      ...qrEditorContext.qrData,
      data: {
        ...qrEditorContext.qrData.data,
        themeColor: {
          ...qrEditorContext.qrData.data.themeColor,
          buttonColor: nextButtonColor,
          backgroundColor: nextBackgroundColor,
        },
      },
    });
  }, [
    debouncedButtonColor,
    debouncedBackgroundColor,
    primaryColor,
    secondaryColor,
    setQrData,
    qrData,
  ]);

  return (
    <div
      suppressHydrationWarning
      data-testid="color-group-theme-picker"
      data-qa="color-group-theme-picker"
      className={`color-group ${className || ""}`}
    >
      <ColorPicker
        id={secondaryColorId}
        label={secondaryColorLabel}
        value={backgroundColor}
        className="color-picker-input"
        placeholder={secondaryColor}
        defaultValue={secondaryColor}
        onChange={handleBackgroundColorChange}
        errorMessage={errors?.themeColor?.backgroundColor?.message as string}
        dataQA={`color-group-theme-picker-element-${secondaryColorId}`}
        dataTestId={`color-group-theme-picker-element-${secondaryColorId}`}
      />

      <ButtonLib
        shape="ghost"
        id="swap-color-button"
        dataQA="color-group-theme-picker-button"
        iconLeft={<FiRefreshCw />}
        ariaLabel="Swap colors"
        onClick={handleSwapColors}
      />
      <ColorPicker
        id={primaryColorId}
        label={primaryColorLabel}
        value={buttonColor}
        className="color-picker-input"
        placeholder={primaryColor}
        defaultValue={primaryColor}
        errorMessage={errors?.themeColor?.buttonColor?.message as string}
        onChange={handleButtonColorChange}
        dataQA={`color-group-theme-picker-element-${primaryColorId}`}
        dataTestId={`color-group-theme-picker-element-${primaryColorId}`}
      />
    </div>
  );
};
